import { Box, List, MenuItem } from "@mui/material";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { useState } from "react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import Input from "../../ui/Input";
import { EditPinDialog } from "./parts/EditPinDialog";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "400px",
};
const center = { lat: 53.3498, lng: -6.2603 }; // Default to Dublin, Ireland
const options = {
  disableDefaultUI: true,
  zoomControl: true,
};

export default function MapWithSearch({
  initialLocation,
  onVenueChange,
  onAddressChange,
  onLocationSelect,
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey:
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
      "AIzaSyC_DfGGmFhYs0EABy_4lE1jPDN0r379XNc",
    libraries,
  });

  const [selected, setSelected] = useState(
    initialLocation.lat && initialLocation.lng
      ? { lat: initialLocation.lat, lng: initialLocation.lng }
      : null,
  );

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <>
      <SearchBox
        initialLocation={initialLocation}
        setSelected={setSelected}
        onVenueChange={onVenueChange} //Remove if not needed
        onAddressChange={onAddressChange}
        onLocationSelect={onLocationSelect}
      />
      <Box sx={{ order: 3 }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={12}
          center={selected || center}
          options={options}
        >
          {selected && <Marker position={selected} />}
        </GoogleMap>
        {selected && (
          <EditPinDialog
            selected={selected}
            onLocationSelect={onLocationSelect}
            setSelected={setSelected}
          />
        )}
      </Box>
    </>
  );
}

function SearchBox({
  initialLocation,
  setSelected,
  // onVenueChange,
  onAddressChange,
  onLocationSelect,
}) {
  const {
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    defaultValue: initialLocation?.address || "",
  });

  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSelect = async (address, venue, placeId) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);

      onAddressChange(address);
      onLocationSelect({ lat, lng, placeId });
      setSelected({ lat, lng });
      // onVenueChange(venue);
    } catch (error) {
      // Handle error if needed
    }
  };

  if (status === "LOADING") {
    return (
      <Box
        sx={{
          width: "100%",
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          border: "1px solid #D4D4D4",
          order: 1,
        }}
      >
        Loading Map...
      </Box>
    );
  }

  if (status === "FAILURE") {
    return (
      <Box
        sx={{
          width: "100%",
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          border: "1px solid #D4D4D4",
          color: "#666",
          order: 1,
        }}
      >
        Failed to load map. Please check your internet connection.
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", marginBottom: 2, order: 1 }}>
      <Input
        label="Address"
        placeholder="Search for an address..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 300)} // Delay to allow click
      />
      {status === "OK" && showSuggestions && (
        <List
          sx={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            bgcolor: "background.paper",
            boxShadow: 3,
            maxHeight: 200,
            overflow: "auto",
            borderRadius: "4px",
          }}
        >
          {data.map(({ place_id, description, structured_formatting }) => (
            <MenuItem
              key={place_id}
              onClick={() => {
                handleSelect(
                  description,
                  structured_formatting.main_text,
                  place_id,
                );
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(24, 144, 255, 0.04)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(24, 144, 255, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(24, 144, 255, 0.12)",
                  },
                },
              }}
            >
              {description}
            </MenuItem>
          ))}
        </List>
      )}
    </Box>
  );
}
